import React, { FC } from "react";
import styled from "@emotion/styled";
import { useParams } from "react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Text, Badge, Card, Flex, Button } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { Uuid } from "@/domains/global/identifiers";

export const BillingViewPage: FC = () => {
  const { spaceAccountId } = useParams<{ spaceAccountId: Uuid }>();

  if (!spaceAccountId) {
    return <div>Invalid Space Account ID</div>;
  }

  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["billing-house-summary", spaceAccountId],
    queryFn: () =>
      api.get("/internal/v1/billing/house/summary/{space_account_id}", {
        params: { path: { space_account_id: spaceAccountId } },
      }),
    retry: 1,
  });

  const summary = data?.data;
  // Use the Space Account page pattern: fetch Stripe Customer ID and construct link
  const spaceAccountDetails = useQuery({
    queryKey: ["space-account-details", spaceAccountId],
    queryFn: () =>
      api.get("/internal/v1/space-accounts/{space_account_id}", {
        params: { path: { space_account_id: spaceAccountId } },
      }),
  });

  const stripeCustomerId = spaceAccountDetails.data?.data?.stripe_customer_id ?? undefined;
  const stripeCustomerLink = stripeCustomerId
    ? `https://dashboard.stripe.com/customers/${stripeCustomerId}`
    : undefined;

  const grantMutation = useMutation({
    mutationFn: async () => {
      return api.post("/internal/v1/billing/house/grant", {
        body: { space_account_id: spaceAccountId },
      });
    },
    onSuccess: () => {
      refetchSummary();
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async () => {
      return api.post("/internal/v1/billing/house/revoke", {
        body: { space_account_id: spaceAccountId },
      });
    },
    onSuccess: () => {
      refetchSummary();
    },
  });

  const canGrant =
    summary?.tier === "free" && (summary?.provider == null || summary?.provider === "UNKNOWN");

  const showRevoke = summary?.provider === "HOUSE";
  const canRevoke = showRevoke && summary?.tier === "paid";

  return (
    <Container>
      <h1>Billing Summary</h1>
      <Text as="p" color="gray">
        Space Account: {spaceAccountId}
      </Text>

      {isLoading && <div>Loading...</div>}
      {isError && (
        <div>
          <div>Error fetching billing summary: {error.message}</div>
          <Button variant="soft" onClick={() => refetchSummary()}>
            Retry
          </Button>
        </div>
      )}

      {summary && (
        <Card size="3" style={{ maxWidth: 600 }}>
          <Flex direction="column" gap="3">
            <Row>
              <Key>Tier</Key>
              <Value>{summary.tier ?? "—"}</Value>
            </Row>
            <Row>
              <Key>Status</Key>
              <Value>{summary.status ? <Badge variant="soft">{summary.status}</Badge> : "—"}</Value>
            </Row>
            <Row>
              <Key>Provider</Key>
              <Value>{summary.provider ?? "—"}</Value>
            </Row>
            {stripeCustomerLink && (
              <Row>
                <Key>Stripe</Key>
                <Value>
                  <a href={stripeCustomerLink} target="_blank" rel="noopener noreferrer">
                    View Stripe Customer
                  </a>
                </Value>
              </Row>
            )}
            <Flex gap="2" mt="3">
              <Button
                onClick={() => grantMutation.mutate()}
                disabled={!canGrant || grantMutation.isPending}
              >
                {grantMutation.isPending
                  ? "Granting…"
                  : canGrant
                    ? "Grant Mem Pro on The House"
                    : "Not eligible for Mem Pro on The House"}
              </Button>
              {showRevoke && (
                <Button
                  color="red"
                  variant="soft"
                  onClick={() => revokeMutation.mutate()}
                  disabled={!canRevoke || revokeMutation.isPending}
                >
                  {revokeMutation.isPending ? "Revoking…" : "Revoke on-the-house Pro"}
                </Button>
              )}
            </Flex>
          </Flex>
        </Card>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Key = styled(Text)`
  color: var(--gray-12);
`;

const Value = styled(Text)`
  font-weight: 500;
`;
